import { isDurablePreviewUrl } from "@/lib/creative-preview";

const CREATIVE_THUMBNAIL_CACHE_VERSION = 1;
const MAX_CACHED_THUMBNAILS = 500;

type ReadableStorage = Pick<Storage, "getItem">;
type WritableStorage = Pick<Storage, "setItem">;

function cacheKey(orgSlug: string) {
  return `aco:creative-thumbnails:v${CREATIVE_THUMBNAIL_CACHE_VERSION}:${orgSlug}`;
}

// Only the app's stored copies outlive the session; provider CDN links expire.
export function isDurableCreativeThumbnailUrl(url: string) {
  return isDurablePreviewUrl(url);
}

export function readCreativeThumbnailCacheSnapshot(
  orgSlug: string,
  storage: ReadableStorage | undefined = typeof window === "undefined"
    ? undefined
    : window.localStorage,
) {
  if (!storage) return "";
  try {
    return storage.getItem(cacheKey(orgSlug)) ?? "";
  } catch {
    return "";
  }
}

export function parseCreativeThumbnailCacheSnapshot(raw: string) {
  const thumbnails = new Map<string, string>();
  if (!raw) return thumbnails;
  try {
    const entries = JSON.parse(raw) as unknown;
    if (!Array.isArray(entries)) return thumbnails;

    for (const entry of entries) {
      if (
        Array.isArray(entry) &&
        typeof entry[0] === "string" &&
        typeof entry[1] === "string" &&
        isDurableCreativeThumbnailUrl(entry[1])
      ) {
        thumbnails.set(entry[0], entry[1]);
      }
    }
  } catch {
    // A corrupt or unavailable browser cache should never block analytics.
  }
  return thumbnails;
}

export function loadCreativeThumbnailCache(
  orgSlug: string,
  storage: ReadableStorage | undefined = typeof window === "undefined"
    ? undefined
    : window.localStorage,
) {
  return parseCreativeThumbnailCacheSnapshot(readCreativeThumbnailCacheSnapshot(orgSlug, storage));
}

export function saveCreativeThumbnailCache(
  orgSlug: string,
  thumbnails: ReadonlyMap<string, string>,
  storage: WritableStorage | undefined = typeof window === "undefined"
    ? undefined
    : window.localStorage,
) {
  if (!storage) return;
  const durableEntries = [...thumbnails]
    .filter(([, url]) => isDurableCreativeThumbnailUrl(url))
    .slice(-MAX_CACHED_THUMBNAILS);
  // React's development Strict Mode mounts effects twice. Do not let the
  // initial empty render erase a warm cache before the post-hydration read has
  // restored it.
  if (durableEntries.length === 0) return;
  try {
    storage.setItem(cacheKey(orgSlug), JSON.stringify(durableEntries));
  } catch {
    // Storage can be disabled or full. The server query remains the fallback.
  }
}
